import { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useIsAdmin } from "@/hooks/use-is-admin";
import {
  adminApi,
  type AdminUserDetail,
  type AdminAiCostMonth,
  type AdminReferralCode,
  type AdminReferralUser,
} from "@/lib/admin-api";
import { startImpersonation } from "@/lib/impersonation";
import { useImpersonation } from "@/hooks/use-impersonation";
import { useLocation } from "wouter";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, Copy, Link2, Plus, Users, Trash2, ToggleLeft, ToggleRight, Shield } from "lucide-react";
import PostsTab from "./admin-posts";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR");
}

function OverviewTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => adminApi.overview(),
  });
  if (isLoading) return <p className="text-muted-foreground">Chargement…</p>;
  if (error)
    return (
      <p className="text-destructive">{(error as Error).message}</p>
    );
  if (!data) return null;
  const cards: Array<[string, string | number]> = [
    ["Utilisateurs total", data.users.total],
    ["Actifs (7j)", data.users.active7Days],
    ["Actifs (30j)", data.users.active30Days],
    ["Utilisateurs désactivés", data.users.disabled],
    ["Tâches total", data.tasks.total],
    ["Projets total", data.projects.total],
    ["Mails total", data.mails.total],
    ["Mails triés", data.mails.triaged],
    ["Appels IA (total)", data.aiCalls.total],
    ["Appels IA (7j)", data.aiCalls.last7Days],
    ["Appels IA (30j)", data.aiCalls.last30Days],
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map(([label, value]) => (
        <div
          key={label}
          className="rounded-lg border bg-card p-4"
          data-testid={`overview-card-${label}`}
        >
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="text-2xl font-semibold mt-1">{value}</div>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const impersonation = useImpersonation();
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  const switchTo = (id: string, email: string) => {
    if (impersonation?.userId === id) return;
    startImpersonation({ userId: id, email });
    qc.clear();
    toast({
      title: "Vue admin activée",
      description: `Vous consultez désormais le compte ${email}.`,
    });
    navigate("/app/today");
  };
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users", page, search],
    queryFn: () => adminApi.listUsers({ page, pageSize: 25, search }),
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="Rechercher par email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          data-testid="admin-users-search"
        />
      </div>
      {isLoading && <p className="text-muted-foreground">Chargement…</p>}
      {error && (
        <p className="text-destructive">{(error as Error).message}</p>
      )}
      {data && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Inscrit le</TableHead>
                <TableHead>Tâches</TableHead>
                <TableHead>Dernière activité</TableHead>
                <TableHead>Vu en ligne</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((u) => (
                <TableRow
                  key={u.id}
                  data-testid={`admin-user-row-${u.id}`}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setOpenUserId(u.id)}
                >
                  <TableCell className="font-medium">{u.email}</TableCell>
                  <TableCell>{formatDate(u.createdAt)}</TableCell>
                  <TableCell>{u.taskCount}</TableCell>
                  <TableCell>{formatDate(u.lastActivityAt)}</TableCell>
                  <TableCell data-testid={`admin-user-last-seen-${u.id}`}>
                    {formatDate(u.lastSeenAt)}
                  </TableCell>
                  <TableCell>
                    {u.disabledAt ? (
                      <span className="text-destructive text-xs">Désactivé</span>
                    ) : (
                      <span className="text-blue-600 text-xs">Actif</span>
                    )}
                  </TableCell>
                  <TableCell
                    className="text-right space-x-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!u.disabledAt && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => switchTo(u.id, u.email)}
                        disabled={impersonation?.userId === u.id}
                        data-testid={`admin-switch-${u.id}`}
                      >
                        {impersonation?.userId === u.id
                          ? "Vue active"
                          : "Voir comme"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenUserId(u.id)}
                      data-testid={`admin-open-${u.id}`}
                    >
                      Détails
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {data.users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Aucun utilisateur
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {data.total} utilisateur(s) — page {data.page}
            </span>
            <div className="space-x-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page * data.pageSize >= data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </>
      )}
      <UserDetailsDialog
        userId={openUserId}
        onClose={() => setOpenUserId(null)}
      />
    </div>
  );
}

function UserDetailsDialog({
  userId,
  onClose,
}: {
  userId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const impersonation = useImpersonation();
  const open = !!userId;
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "user-detail", userId],
    queryFn: () => adminApi.getUser(userId as string),
    enabled: open,
  });

  const [plan, setPlan] = useState<"free" | "pro" | "unlimited">("free");
  const [credits, setCredits] = useState<string>("");
  const [cap, setCap] = useState<string>("");
  const [grantKind, setGrantKind] = useState<"credits" | "discount">("credits");
  const [grantAmount, setGrantAmount] = useState<string>("");
  const [grantReason, setGrantReason] = useState<string>("");

  useEffect(() => {
    if (data) {
      setPlan(data.plan as "free" | "pro" | "unlimited");
      setCredits(String(data.aiCreditsRemaining));
      setCap(String(data.rawDailyCap));
    }
  }, [data?.id, data?.plan, data?.aiCreditsRemaining, data?.rawDailyCap]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "user-detail", userId] });
    qc.invalidateQueries({ queryKey: ["admin", "users"] });
    qc.invalidateQueries({ queryKey: ["admin", "billing-events"] });
  };

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof adminApi.patchUser>[1]) =>
      adminApi.patchUser(userId as string, body),
    onSuccess: () => {
      toast({ title: "Compte mis à jour" });
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Erreur",
        description: (e as Error).message,
        variant: "destructive",
      }),
  });

  const grant = useMutation({
    mutationFn: (body: Parameters<typeof adminApi.grantUser>[1]) =>
      adminApi.grantUser(userId as string, body),
    onSuccess: () => {
      toast({ title: "Geste commercial enregistré" });
      setGrantAmount("");
      setGrantReason("");
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Erreur",
        description: (e as Error).message,
        variant: "destructive",
      }),
  });

  const disable = useMutation({
    mutationFn: () => adminApi.disableUser(userId as string),
    onSuccess: () => {
      toast({ title: "Compte désactivé" });
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Erreur",
        description: (e as Error).message,
        variant: "destructive",
      }),
  });
  const enable = useMutation({
    mutationFn: () => adminApi.enableUser(userId as string),
    onSuccess: () => {
      toast({ title: "Compte réactivé" });
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Erreur",
        description: (e as Error).message,
        variant: "destructive",
      }),
  });
  const resetQuota = useMutation({
    mutationFn: () => adminApi.resetAiQuota(userId as string),
    onSuccess: (r) => {
      toast({
        title: "Quota IA réinitialisé",
        description: `${r.deletedRows} appel(s) supprimé(s) pour aujourd'hui`,
      });
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Erreur",
        description: (e as Error).message,
        variant: "destructive",
      }),
  });

  const giftPlan = useMutation({
    mutationFn: (gifted: boolean) =>
      adminApi.patchUser(userId as string, { planGifted: gifted }),
    onSuccess: (_, gifted) => {
      toast({
        title: gifted ? "Plan offert activé" : "Plan offert retiré",
        description: gifted
          ? "La facturation est masquée pour cet utilisateur."
          : "L'utilisateur revoit la section facturation normalement.",
      });
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Erreur",
        description: (e as Error).message,
        variant: "destructive",
      }),
  });

  const handleClose = (next: boolean) => {
    if (!next) {
      setPlan("free");
      setCredits("");
      setCap("");
      setGrantKind("credits");
      setGrantAmount("");
      setGrantReason("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="admin-user-dialog"
      >
        <DialogHeader>
          <DialogTitle>Détails utilisateur</DialogTitle>
          <DialogDescription>
            {data?.email ?? (isLoading ? "Chargement…" : "")}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className="text-destructive">{(error as Error).message}</p>
        )}
        {data && (
          <div className="space-y-6">
            <SummaryGrid detail={data} />

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Plan & quotas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Plan</Label>
                  <Select
                    value={plan}
                    onValueChange={(v) =>
                      setPlan(v as "free" | "pro" | "unlimited")
                    }
                  >
                    <SelectTrigger data-testid="admin-edit-plan">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">free</SelectItem>
                      <SelectItem value="pro">pro</SelectItem>
                      <SelectItem value="unlimited">unlimited</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Crédits IA restants</Label>
                  <Input
                    type="number"
                    value={credits}
                    onChange={(e) => setCredits(e.target.value)}
                    data-testid="admin-edit-credits"
                  />
                  <div className="flex gap-1 pt-1">
                    {[100, 500].map((delta) => (
                      <Button
                        key={delta}
                        size="sm"
                        variant="outline"
                        type="button"
                        onClick={() =>
                          setCredits((c) =>
                            String((Number(c) || 0) + delta),
                          )
                        }
                        data-testid={`admin-credits-add-${delta}`}
                      >
                        +{delta}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Plafond quotidien (par compte mail)</Label>
                  <Input
                    type="number"
                    value={cap}
                    onChange={(e) => setCap(e.target.value)}
                    data-testid="admin-edit-cap"
                  />
                </div>
              </div>
              <Button
                size="sm"
                onClick={() =>
                  patch.mutate({
                    plan,
                    aiCreditsRemaining: Number(credits),
                    dailyAiAnalysisCap: Number(cap),
                  })
                }
                disabled={patch.isPending}
                data-testid="admin-save-plan"
              >
                Enregistrer
              </Button>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Geste commercial</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select
                    value={grantKind}
                    onValueChange={(v) =>
                      setGrantKind(v as "credits" | "discount")
                    }
                  >
                    <SelectTrigger data-testid="admin-grant-kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credits">Crédits offerts</SelectItem>
                      <SelectItem value="discount">
                        Remise (€, factice)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Montant</Label>
                  <Input
                    type="number"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    data-testid="admin-grant-amount"
                  />
                </div>
                <div className="space-y-1 sm:col-span-1">
                  <Label>Motif</Label>
                  <Input
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    placeholder="Geste commercial…"
                    data-testid="admin-grant-reason"
                  />
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  grant.mutate({
                    kind: grantKind,
                    amount: Number(grantAmount),
                    reason: grantReason,
                  })
                }
                disabled={
                  grant.isPending ||
                  !grantAmount ||
                  Number(grantAmount) <= 0 ||
                  !grantReason.trim()
                }
                data-testid="admin-grant-submit"
              >
                Appliquer
              </Button>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Actions sur le compte</h3>
              <div className="flex flex-wrap gap-2">
                {data.disabledAt ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => enable.mutate()}
                    disabled={enable.isPending}
                  >
                    Réactiver
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => disable.mutate()}
                    disabled={disable.isPending}
                  >
                    Désactiver
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => resetQuota.mutate()}
                  disabled={resetQuota.isPending}
                >
                  Reset quota IA du jour
                </Button>
                {data.planGifted ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => giftPlan.mutate(false)}
                    disabled={giftPlan.isPending}
                    data-testid="admin-ungift-plan"
                  >
                    Retirer le plan offert
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => giftPlan.mutate(true)}
                    disabled={giftPlan.isPending}
                    data-testid="admin-gift-plan"
                  >
                    🎁 Offrir le plan
                  </Button>
                )}
                {!data.disabledAt && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={impersonation?.userId === data.id}
                    onClick={() => {
                      if (impersonation?.userId === data.id) return;
                      startImpersonation({
                        userId: data.id,
                        email: data.email,
                      });
                      qc.clear();
                      toast({
                        title: "Vue admin activée",
                        description: `Vous consultez désormais le compte ${data.email}.`,
                      });
                      onClose();
                      navigate("/app/today");
                    }}
                    data-testid="admin-modal-impersonate"
                  >
                    {impersonation?.userId === data.id
                      ? "Vue active"
                      : "Voir comme cet utilisateur"}
                  </Button>
                )}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">
                Derniers événements de facturation
              </h3>
              {data.recentBillingEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucun événement.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Acteur</TableHead>
                      <TableHead>Détails</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recentBillingEvents.map((ev) => (
                      <TableRow key={ev.id}>
                        <TableCell>{formatDate(ev.createdAt)}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {ev.type}
                        </TableCell>
                        <TableCell className="text-xs">
                          {ev.actorIsAdmin ? "Admin" : "Utilisateur"}
                          {ev.actorEmail ? ` (${ev.actorEmail})` : ""}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {ev.payload ? JSON.stringify(ev.payload) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryGrid({ detail }: { detail: AdminUserDetail }) {
  const items: Array<[string, string]> = [
    ["Plan", detail.plan],
    [
      "Crédits IA",
      detail.dailyCap === null
        ? "illimité (admin)"
        : String(detail.aiCreditsRemaining),
    ],
    [
      "Plafond / compte",
      detail.dailyCap === null ? "illimité (admin)" : String(detail.rawDailyCap),
    ],
    ["Comptes mail", String(detail.mailAccountCount)],
    ["Utilisé aujourd'hui", String(detail.usedToday)],
    ["Inscrit le", formatDate(detail.createdAt)],
    ["Vu en ligne", formatDate(detail.lastSeenAt)],
    ["Dernière activité", formatDate(detail.lastActivityAt)],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
      {items.map(([k, v]) => (
        <div key={k} className="space-y-0.5">
          <div className="text-muted-foreground text-xs">{k}</div>
          <div className="font-medium truncate" title={v}>
            {v}
          </div>
        </div>
      ))}
    </div>
  );
}

function AiQuotasTab() {
  const [days, setDays] = useState(7);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "ai-usage", days],
    queryFn: () => adminApi.aiUsage(days),
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <span>Période :</span>
        {[1, 7, 30].map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            onClick={() => setDays(d)}
          >
            {d}j
          </Button>
        ))}
      </div>
      {isLoading && <p className="text-muted-foreground">Chargement…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}
      {data && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Appels (période)</TableHead>
              <TableHead className="text-right">Aujourd'hui</TableHead>
              <TableHead className="text-right">Quota</TableHead>
              <TableHead className="text-right">Saturation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r) => (
              <TableRow
                key={r.userId}
                data-testid={`ai-quota-row-${r.userId}`}
                className={r.overEightyPct ? "bg-amber-50" : undefined}
              >
                <TableCell>{r.email ?? r.userId}</TableCell>
                <TableCell className="text-right font-mono">
                  {r.callCount}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {r.usedToday}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {r.dailyCap}
                </TableCell>
                <TableCell className="text-right font-mono">
                  <span
                    className={
                      r.overEightyPct
                        ? "text-amber-600 font-semibold"
                        : "text-muted-foreground"
                    }
                    data-testid={`ai-quota-saturation-${r.userId}`}
                  >
                    {Math.round(r.saturation * 100)}%
                    {r.overEightyPct ? " ⚠" : ""}
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {data.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  Aucun appel sur la période
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function BillingEventsTab() {
  const [page, setPage] = useState(1);
  const [days, setDays] = useState(30);
  const [type, setType] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "billing-events", page, days, type, userId],
    queryFn: () =>
      adminApi.billingEvents({
        page,
        pageSize: 50,
        days,
        type: type || undefined,
        userId: userId || undefined,
      }),
  });
  const types = [
    "upgrade",
    "credits_purchase",
    "admin_grant",
    "admin_discount",
    "plan_change",
    "cap_change",
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
        <div className="space-y-1">
          <Label>Période (jours)</Label>
          <Select
            value={String(days)}
            onValueChange={(v) => {
              setDays(Number(v));
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 7, 30, 90, 365].map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {d}j
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Type</Label>
          <Select
            value={type || "all"}
            onValueChange={(v) => {
              setType(v === "all" ? "" : v);
              setPage(1);
            }}
          >
            <SelectTrigger data-testid="billing-events-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {types.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label>Filtrer par user ID</Label>
          <Input
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setPage(1);
            }}
            placeholder="UUID utilisateur (optionnel)"
            data-testid="billing-events-user"
          />
        </div>
      </div>
      {isLoading && <p className="text-muted-foreground">Chargement…</p>}
      {error && (
        <p className="text-destructive">{(error as Error).message}</p>
      )}
      {data && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Acteur</TableHead>
                <TableHead>Détails</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((ev) => (
                <TableRow
                  key={ev.id}
                  data-testid={`billing-event-${ev.id}`}
                >
                  <TableCell>{formatDate(ev.createdAt)}</TableCell>
                  <TableCell className="text-xs">
                    {ev.userEmail ?? ev.userId}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {ev.type}
                  </TableCell>
                  <TableCell className="text-xs">
                    {ev.actorIsAdmin ? "Admin" : "Utilisateur"}
                    {ev.actorEmail ? ` (${ev.actorEmail})` : ""}
                  </TableCell>
                  <TableCell className="text-xs font-mono max-w-md truncate">
                    {ev.payload ? JSON.stringify(ev.payload) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-8"
                  >
                    Aucun événement
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {data.total} événement(s) — page {data.page}
            </span>
            <div className="space-x-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page * data.pageSize >= data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ActivityTab() {
  const [days, setDays] = useState(7);
  const [userId, setUserId] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "activity", days, userId],
    queryFn: () =>
      adminApi.activity({ days, userId: userId.trim() || undefined }),
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>Période :</span>
        {[1, 7, 30].map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            onClick={() => setDays(d)}
            data-testid={`activity-days-${d}`}
          >
            {d}j
          </Button>
        ))}
        <Input
          placeholder="Filtrer par userId (optionnel)…"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="max-w-xs"
          data-testid="activity-user-filter"
        />
      </div>
      {isLoading && <p className="text-muted-foreground">Chargement…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}
      {data && (
        <>
          {data.byKind.length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs">
              {data.byKind.map((k) => (
                <span
                  key={k.kind}
                  className="rounded-md border bg-muted/40 px-2 py-1 font-mono"
                  data-testid={`activity-kind-${k.kind}`}
                >
                  {k.kind} · {k.eventCount}
                </span>
              ))}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quand</TableHead>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Détails</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow
                  key={r.id}
                  data-testid={`activity-row-${r.id}`}
                >
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {formatDate(r.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.email ?? r.userId}
                  </TableCell>
                  <TableCell>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                      {r.kind}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground max-w-md truncate">
                    {r.payload ? JSON.stringify(r.payload) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-muted-foreground py-8"
                  >
                    Aucune activité sur la période
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}

function MailFailuresTab() {
  const [days, setDays] = useState(7);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "mail-failures", days],
    queryFn: () => adminApi.mailFailures(days),
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <span>Période :</span>
        {[1, 7, 30].map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            onClick={() => setDays(d)}
          >
            {d}j
          </Button>
        ))}
      </div>
      {isLoading && <p className="text-muted-foreground">Chargement…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}
      {data && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-2">Par utilisateur</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Échecs</TableHead>
                  <TableHead>Dernier échec</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.grouped.map((g) => (
                  <TableRow key={g.userId}>
                    <TableCell>{g.email ?? g.userId}</TableCell>
                    <TableCell className="text-right font-mono">
                      {g.failureCount}
                    </TableCell>
                    <TableCell className="text-xs">
                      {formatDate(g.lastFailureAt)}
                    </TableCell>
                  </TableRow>
                ))}
                {data.grouped.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                      Aucun échec sur la période
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">Échecs récents</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Message ID</TableHead>
                  <TableHead>Erreur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {formatDate(f.createdAt)}
                    </TableCell>
                    <TableCell className="text-xs">{f.email ?? f.userId}</TableCell>
                    <TableCell className="text-xs font-mono truncate max-w-[200px]">
                      {f.messageId}
                    </TableCell>
                    <TableCell className="text-xs text-destructive">
                      {f.error}
                    </TableCell>
                  </TableRow>
                ))}
                {data.recent.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                      Aucun échec
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

interface SupportRequestRow {
  id: string;
  userId: string | null;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  body: string;
  category: string;
  status: string;
  emailSent: string;
  emailError: string | null;
  createdAt: string;
}

function SupportRequestsTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "support-requests"],
    queryFn: async () => {
      const res = await fetch("/api/admin/support-requests?pageSize=100", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as {
        page: number;
        pageSize: number;
        total: number;
        rows: SupportRequestRow[];
      };
    },
  });
  if (isLoading) return <p className="text-muted-foreground">Chargement…</p>;
  if (error)
    return <p className="text-destructive">{(error as Error).message}</p>;
  if (!data) return null;

  if (data.rows.length === 0) {
    return (
      <p className="text-muted-foreground">
        Aucune demande de support pour le moment.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {data.total} demande{data.total > 1 ? "s" : ""} reçue
        {data.total > 1 ? "s" : ""}.
      </p>
      <div className="space-y-3">
        {data.rows.map((r) => (
          <div
            key={r.id}
            className="rounded-xl border p-4 bg-card"
            data-testid="support-request-row"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold text-sm">{r.subject}</p>
                <p className="text-xs text-muted-foreground">
                  {r.fromName ? `${r.fromName} <${r.fromEmail}>` : r.fromEmail}
                  {" · "}
                  {formatDate(r.createdAt)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                  {r.category}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    r.emailSent === "sent"
                      ? "bg-blue-100 text-blue-800"
                      : r.emailSent === "failed"
                        ? "bg-red-100 text-red-800"
                        : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {r.emailSent === "sent"
                    ? "E-mail envoyé"
                    : r.emailSent === "failed"
                      ? "Échec e-mail"
                      : r.emailSent === "skipped"
                        ? "SMTP non configuré"
                        : r.emailSent}
                </span>
              </div>
            </div>
            <pre className="mt-3 whitespace-pre-wrap text-sm bg-muted/50 rounded-lg p-3 font-sans">
              {r.body}
            </pre>
            {r.emailError && (
              <p className="mt-2 text-xs text-destructive">
                Erreur e-mail : {r.emailError}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatEur(n: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
}

function AiCostsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "ai-costs"],
    queryFn: () => adminApi.aiCosts(),
    refetchInterval: 60_000,
  });

  const reset = useMutation({
    mutationFn: () => adminApi.aiCostsReset(),
    onSuccess: () => {
      toast({ title: "Compteur du mois remis à zéro." });
      qc.invalidateQueries({ queryKey: ["admin", "ai-costs"] });
    },
    onError: (e: Error) =>
      toast({ title: "Échec", description: e.message, variant: "destructive" }),
  });
  const trip = useMutation({
    mutationFn: () => adminApi.aiCostsTrip(),
    onSuccess: () => {
      toast({ title: "Coupe-circuit IA armé manuellement." });
      qc.invalidateQueries({ queryKey: ["admin", "ai-costs"] });
    },
    onError: (e: Error) =>
      toast({ title: "Échec", description: e.message, variant: "destructive" }),
  });
  const release = useMutation({
    mutationFn: () => adminApi.aiCostsRelease(),
    onSuccess: () => {
      toast({ title: "Coupe-circuit IA relâché." });
      qc.invalidateQueries({ queryKey: ["admin", "ai-costs"] });
    },
    onError: (e: Error) =>
      toast({ title: "Échec", description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <p className="text-muted-foreground">Chargement…</p>;
  if (error)
    return <p className="text-destructive">{(error as Error).message}</p>;
  if (!data) return null;

  const cur = data.current;
  const pct = Math.min(100, Math.round(cur.percentOfBudget * 100));
  const barColor =
    pct >= 100
      ? "bg-red-600"
      : pct >= 80
        ? "bg-amber-500"
        : "bg-blue-600";
  const over80 = pct >= 80 && pct < 100;
  const over100 = pct >= 100;

  return (
    <div className="space-y-6">
      {over100 && (
        <div
          role="alert"
          data-testid="ai-costs-banner-100"
          className="flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-900"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">
              Budget IA mensuel atteint — coupe-circuit armé
            </p>
            <p className="mt-1">
              Toutes les requêtes IA renvoient 503 jusqu'à la fin du
              mois ou jusqu'à ce que vous relâchiez manuellement le
              coupe-circuit ci-dessous.
            </p>
          </div>
        </div>
      )}
      {over80 && (
        <div
          role="alert"
          data-testid="ai-costs-banner-80"
          className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">
              Seuil 80% du budget IA franchi ({pct}%)
            </p>
            <p className="mt-1">
              Une alerte e-mail a été envoyée à l'administrateur.
              Surveillez la consommation : à 100% le coupe-circuit
              s'armera automatiquement.
            </p>
          </div>
        </div>
      )}
      <div
        className="rounded-xl border p-4 bg-card space-y-3"
        data-testid="ai-costs-current"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm text-muted-foreground">
              Mois en cours · {cur.yearMonth}
            </p>
            <p className="text-2xl font-semibold">
              {formatEur(cur.totalEur)}{" "}
              <span className="text-sm text-muted-foreground font-normal">
                / {formatEur(cur.budgetEur)} ({pct}%)
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {cur.callCount} appel{cur.callCount > 1 ? "s" : ""} ·{" "}
              {cur.promptTokens.toLocaleString("fr-FR")} tokens entrée ·{" "}
              {cur.completionTokens.toLocaleString("fr-FR")} tokens sortie
            </p>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full ${
              cur.killSwitchArmed
                ? "bg-red-100 text-red-800"
                : "bg-blue-100 text-blue-800"
            }`}
            data-testid="ai-killswitch-state"
          >
            {cur.killSwitchArmed
              ? "Coupe-circuit ARMÉ"
              : "Service IA actif"}
          </span>
        </div>
        <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {cur.manuallyTrippedAt && (
          <p className="text-xs text-muted-foreground">
            Armé manuellement le {formatDate(cur.manuallyTrippedAt)}
            {cur.manuallyTrippedBy
              ? ` par ${cur.manuallyTrippedBy}`
              : ""}.
          </p>
        )}
        {cur.alertedAt80 && (
          <p className="text-xs text-muted-foreground">
            Alerte 80% envoyée le {formatDate(cur.alertedAt80)}.
          </p>
        )}
        <div className="flex gap-2 flex-wrap pt-2">
          {cur.killSwitchArmed ? (
            <Button
              size="sm"
              onClick={() => release.mutate()}
              disabled={release.isPending}
              data-testid="btn-ai-release"
            >
              Relâcher le coupe-circuit
            </Button>
          ) : (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => trip.mutate()}
              disabled={trip.isPending}
              data-testid="btn-ai-trip"
            >
              Armer manuellement
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (
                window.confirm(
                  "Remettre à zéro les compteurs du mois en cours ?",
                )
              ) {
                reset.mutate();
              }
            }}
            disabled={reset.isPending}
            data-testid="btn-ai-reset"
          >
            Remettre à zéro
          </Button>
        </div>
      </div>

      <div className="space-y-2" data-testid="ai-costs-breakdown">
        <h3 className="text-sm font-medium">
          Répartition par fonctionnalité (mois en cours)
        </h3>
        {data.breakdown.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun appel enregistré ce mois-ci.
          </p>
        ) : (
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2">Fonctionnalité</th>
                  <th className="px-3 py-2 text-right">Dépense</th>
                  <th className="px-3 py-2 text-right">Part</th>
                  <th className="px-3 py-2 text-right">Appels</th>
                  <th className="px-3 py-2 text-right">Tokens in/out</th>
                </tr>
              </thead>
              <tbody>
                {data.breakdown.map((row) => {
                  const share =
                    cur.totalEur > 0
                      ? Math.round((row.totalEur / cur.totalEur) * 100)
                      : 0;
                  return (
                    <tr
                      key={row.feature}
                      className="border-t"
                      data-testid={`ai-feature-row-${row.feature}`}
                    >
                      <td className="px-3 py-2 font-medium">{row.feature}</td>
                      <td className="px-3 py-2 text-right">
                        {formatEur(row.totalEur)}
                      </td>
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        {share}%
                      </td>
                      <td className="px-3 py-2 text-right">{row.callCount}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        {row.promptTokens.toLocaleString("fr-FR")} /{" "}
                        {row.completionTokens.toLocaleString("fr-FR")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Historique (6 derniers mois)</h3>
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-3 py-2">Mois</th>
                <th className="px-3 py-2 text-right">Dépense</th>
                <th className="px-3 py-2 text-right">Appels</th>
                <th className="px-3 py-2 text-right">Tokens in/out</th>
                <th className="px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((m: AdminAiCostMonth) => (
                <tr key={m.yearMonth} className="border-t">
                  <td className="px-3 py-2">{m.yearMonth}</td>
                  <td className="px-3 py-2 text-right">
                    {formatEur(m.totalEur)}
                  </td>
                  <td className="px-3 py-2 text-right">{m.callCount}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">
                    {m.promptTokens.toLocaleString("fr-FR")} /{" "}
                    {m.completionTokens.toLocaleString("fr-FR")}
                  </td>
                  <td className="px-3 py-2">
                    {m.killSwitchArmed ? (
                      <span className="text-red-700">Suspendu</span>
                    ) : (
                      <span className="text-muted-foreground">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RegistreTraitementsTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "registre-traitements"],
    queryFn: async () => {
      const res = await fetch("/api/admin/registre-traitements", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as { content: string };
    },
  });
  if (isLoading) return <p className="text-muted-foreground">Chargement…</p>;
  if (error)
    return <p className="text-destructive">{(error as Error).message}</p>;
  if (!data) return null;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Source : <code>docs/rgpd/registre-traitements.md</code>. Pour le
        modifier, éditez ce fichier dans le dépôt.
      </p>
      <pre className="whitespace-pre-wrap text-sm bg-muted/30 rounded-xl p-4 font-mono leading-relaxed border">
        {data.content}
      </pre>
    </div>
  );
}

function referralLink(code: string): string {
  const base = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
  return `${window.location.origin}${base}/sign-up?ref=${code}`;
}

function CreateReferralDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");

  function generateCode() {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let c = "";
    for (let i = 0; i < 8; i++)
      c += chars[Math.floor(Math.random() * chars.length)];
    setCode(c);
  }

  useEffect(() => {
    if (open) {
      generateCode();
      setLabel("");
      setNotes("");
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.createReferral({
        code: code.toUpperCase().trim(),
        label: label.trim(),
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "referrals"] });
      toast({ title: "Code créé", description: `Le code ${code} est actif.` });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erreur",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Créer un code référent</DialogTitle>
          <DialogDescription>
            Le lien généré peut être partagé avec votre apporteur d'affaires.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Code</Label>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="EX: MARTIN2024"
                className="font-mono uppercase"
              />
              <Button variant="outline" size="sm" onClick={generateCode}>
                ↻
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Lettres majuscules, chiffres, _ et - uniquement.
            </p>
          </div>
          <div className="space-y-1">
            <Label>Nom de l'apporteur</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Martin Dupont – Cabinet XYZ"
            />
          </div>
          <div className="space-y-1">
            <Label>Notes (optionnel)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Taux de commission, contexte…"
            />
          </div>
          {code && (
            <div className="rounded-md bg-muted px-3 py-2 text-xs font-mono break-all text-muted-foreground">
              {referralLink(code)}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={
              !code.trim() ||
              !label.trim() ||
              mutation.isPending ||
              !/^[A-Z0-9_-]{2,50}$/i.test(code.trim())
            }
          >
            {mutation.isPending ? "Création…" : "Créer le code"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReferralUsersDialog({
  referral,
  onClose,
}: {
  referral: AdminReferralCode | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referral-users", referral?.id],
    queryFn: () => adminApi.listReferralUsers(referral!.id),
    enabled: !!referral,
  });

  return (
    <Dialog open={!!referral} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Inscrits via {referral?.code}{" "}
            <span className="text-muted-foreground font-normal text-sm">
              — {referral?.label}
            </span>
          </DialogTitle>
          <DialogDescription>
            {data?.length ?? 0} inscription
            {(data?.length ?? 0) !== 1 ? "s" : ""} via ce code
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : !data?.length ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Aucun utilisateur inscrit via ce code pour l'instant.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Forfait</TableHead>
                <TableHead>Inscrit le</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data as AdminReferralUser[]).map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell>{u.name ?? "—"}</TableCell>
                  <TableCell>{u.plan}</TableCell>
                  <TableCell>{formatDate(u.createdAt)}</TableCell>
                  <TableCell>
                    {u.disabledAt ? (
                      <span className="text-xs text-red-600">Désactivé</span>
                    ) : (
                      <span className="text-xs text-green-600">Actif</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReferentsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] =
    useState<AdminReferralCode | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "referrals"],
    queryFn: () => adminApi.listReferrals(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      adminApi.patchReferral(id, { active }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin", "referrals"] }),
    onError: (err: Error) =>
      toast({ title: "Erreur", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteReferral(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "referrals"] });
      toast({ title: "Code supprimé" });
    },
    onError: (err: Error) =>
      toast({ title: "Erreur", description: err.message, variant: "destructive" }),
  });

  function copyLink(code: string) {
    navigator.clipboard
      .writeText(referralLink(code))
      .then(() => toast({ title: "Lien copié !" }))
      .catch(() =>
        toast({ title: "Impossible de copier", variant: "destructive" }),
      );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Impossible de charger les codes référents.
      </div>
    );
  }

  const codes = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Codes référents</h2>
          <p className="text-sm text-muted-foreground">
            Créez des codes pour vos apporteurs d'affaires et suivez les
            inscriptions.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Créer un code
        </Button>
      </div>

      {codes.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
          <Link2 className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm font-medium">Aucun code référent</p>
          <p className="text-xs text-muted-foreground mt-1">
            Créez votre premier code pour commencer à suivre les apporteurs
            d'affaires.
          </p>
          <Button
            variant="outline"
            className="mt-4 gap-2"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Créer un code
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Apporteur</TableHead>
              <TableHead className="text-center">Inscrits</TableHead>
              <TableHead>Dernière inscription</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.map((c) => (
              <TableRow key={c.id} className={c.active ? "" : "opacity-50"}>
                <TableCell>
                  <span className="font-mono font-semibold tracking-wide">
                    {c.code}
                  </span>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm font-medium">{c.label}</p>
                    {c.notes && (
                      <p className="text-xs text-muted-foreground">{c.notes}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <button
                    onClick={() => setSelectedReferral(c)}
                    className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-sm font-medium hover:bg-secondary/80 transition-colors"
                    title="Voir les inscrits"
                  >
                    <Users className="h-3.5 w-3.5" />
                    {c.signupCount}
                  </button>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {c.lastSignupAt ? formatDate(c.lastSignupAt) : "—"}
                </TableCell>
                <TableCell>
                  {c.active ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 inline-block" />
                      Inactif
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Copier le lien d'inscription"
                      onClick={() => copyLink(c.code)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={c.active ? "Désactiver" : "Activer"}
                      onClick={() =>
                        toggleMutation.mutate({ id: c.id, active: !c.active })
                      }
                    >
                      {c.active ? (
                        <ToggleRight className="h-4 w-4 text-green-600" />
                      ) : (
                        <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Supprimer ce code"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Supprimer le code ${c.code} ? Cette action est irréversible.`,
                          )
                        ) {
                          deleteMutation.mutate(c.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CreateReferralDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <ReferralUsersDialog
        referral={selectedReferral}
        onClose={() => setSelectedReferral(null)}
      />
    </div>
  );
}

const ADMIN_TABS = [
  { value: "overview",  label: "Vue d'ensemble", testId: "tab-overview"  },
  { value: "users",     label: "Utilisateurs",    testId: "tab-users"     },
  { value: "ai",        label: "Quotas IA",       testId: "tab-ai"        },
  { value: "ai-costs",  label: "Coûts IA",        testId: "tab-ai-costs"  },
  { value: "mail",      label: "Échecs mail",     testId: "tab-mail"      },
  { value: "activity",  label: "Activité",        testId: "tab-activity"  },
  { value: "payments",  label: "Paiements",       testId: "tab-payments"  },
  { value: "support",   label: "Support",         testId: "tab-support"   },
  { value: "referents", label: "Référents",       testId: "tab-referents" },
  { value: "rgpd",      label: "RGPD",            testId: "tab-rgpd"      },
  { value: "posts",     label: "Posts",           testId: "tab-posts"     },
] as const;

type AdminTab = (typeof ADMIN_TABS)[number]["value"];

export default function AdminPage({ defaultTab }: { defaultTab?: AdminTab } = {}) {
  const { isLoaded, isAdmin } = useIsAdmin();
  const [activeTab, setActiveTab] = useState<AdminTab>(defaultTab ?? "overview");

  if (!isLoaded) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAdmin) return <Redirect to="/app/today" />;

  const activeLabel = ADMIN_TABS.find((t) => t.value === activeTab)?.label ?? "";

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Admin header ─────────────────────────────────── */}
      <div className="border-b border-border bg-muted/30 px-4 md:px-8 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Shield className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold leading-none tracking-tight">
              Administration
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              OneNext — panneau d'administration
            </p>
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as AdminTab)}
        className="flex flex-col flex-1"
      >
        {/* ── Mobile: Select dropdown ───────────────────── */}
        <div className="md:hidden border-b border-border bg-background px-4 py-3">
          <Select value={activeTab} onValueChange={(v) => setActiveTab(v as AdminTab)}>
            <SelectTrigger className="w-full" data-testid="admin-tab-select">
              <SelectValue>{activeLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {ADMIN_TABS.map((tab) => (
                <SelectItem key={tab.value} value={tab.value} data-testid={tab.testId}>
                  {tab.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* ── Desktop: scrollable pill tabs ────────────── */}
        <div className="hidden md:block border-b border-border bg-background">
          <div className="max-w-6xl mx-auto px-8">
            <TabsList className="h-auto gap-0 bg-transparent p-0 rounded-none flex w-full justify-start overflow-x-auto">
              {ADMIN_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  data-testid={tab.testId}
                  className="relative h-10 shrink-0 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:bg-transparent hover:text-foreground"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        {/* ── Content ──────────────────────────────────── */}
        <div className="flex-1 px-4 md:px-8 py-6">
          <div className="max-w-6xl mx-auto">
            <TabsContent value="overview" className="mt-0">
              <OverviewTab />
            </TabsContent>
            <TabsContent value="users" className="mt-0">
              <UsersTab />
            </TabsContent>
            <TabsContent value="ai" className="mt-0">
              <AiQuotasTab />
            </TabsContent>
            <TabsContent value="ai-costs" className="mt-0">
              <AiCostsTab />
            </TabsContent>
            <TabsContent value="mail" className="mt-0">
              <MailFailuresTab />
            </TabsContent>
            <TabsContent value="activity" className="mt-0">
              <ActivityTab />
            </TabsContent>
            <TabsContent value="payments" className="mt-0">
              <BillingEventsTab />
            </TabsContent>
            <TabsContent value="support" className="mt-0">
              <SupportRequestsTab />
            </TabsContent>
            <TabsContent value="referents" className="mt-0">
              <ReferentsTab />
            </TabsContent>
            <TabsContent value="rgpd" className="mt-0">
              <RegistreTraitementsTab />
            </TabsContent>
            <TabsContent value="posts" className="mt-0">
              <PostsTab />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
